export interface StrategyCodeRef {
  code: string
  label: string
}

/** One dropdown row: codes sharing a calendar group (e.g. 3, 4, 7) collapse into one. */
export interface StrategyOption {
  value: string
  codes: string[]
  label: string
  hasCalendar: boolean
}

const byCode = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true })

/**
 * Collapse the reference codes into one option per calendar group.
 *
 * The calendar's group label wins so the dropdown matches the transit table and
 * the letter; the reference-data label is only a fallback for codes the calendar
 * does not cover. A group's value is `preferred` when it is a member (the vendor's
 * default, so picking the group clears the override), else its lowest code.
 * The backend matches every member to the same calendar group.
 */
export function groupStrategyOptions(
  codes: StrategyCodeRef[],
  calendarLabels: Record<string, string[]>,
  preferred?: string | null
): StrategyOption[] {
  const groups = new Map<string, StrategyOption>()
  for (const { code, label } of codes) {
    const calendarLabel = calendarLabels[code]?.join(' / ')
    const key = calendarLabel ? `cal:${calendarLabel}` : `code:${code}`
    const group = groups.get(key)
    if (group) {
      if (!group.codes.includes(code)) group.codes.push(code)
    } else {
      groups.set(key, {
        value: code,
        codes: [code],
        label: calendarLabel || label,
        hasCalendar: !!calendarLabel,
      })
    }
  }
  const options = [...groups.values()]
  for (const option of options) {
    option.codes.sort(byCode)
    option.value =
      preferred && option.codes.includes(preferred)
        ? preferred
        : option.codes[0]
  }
  return options.sort((a, b) => byCode(a.codes[0], b.codes[0]))
}

/** The option a code belongs to, if any. */
export function optionFor(
  options: StrategyOption[],
  code: string | null | undefined
): StrategyOption | undefined {
  return code ? options.find(o => o.codes.includes(code)) : undefined
}

/** "3 / 4 / 7" for a group, the bare code otherwise. */
export function optionCodes(option: StrategyOption): string {
  return option.codes.join(' / ')
}
